import {app, platform, systemPreferences} from '@todesktop/client-core';
import {appLog, appLogO} from './util';
import {TransitionModal} from './transition-modal';
import {useEffect, useState} from 'react';

interface CaptureSourceGridProps {
  sources: app.CaptureSource[];
  handleSelectSource: (source: app.CaptureSource) => void;
}

function CaptureSourceGrid({sources, handleSelectSource}: CaptureSourceGridProps) {
  return (
    <div className="mt-2 text-sm grid grid-cols-2 text-center">
      {sources.map(source => (
        <button className="pb-1 pt-1 hover:bg-gray-200" onClick={() => handleSelectSource(source)}>
          <img className="m-auto" src={source.thumbnail} />
          {source.name}
        </button>
      ))}
    </div>
  );
}

interface DesktopSourcePickerProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  setScreenShareSourceId: (sourceId: string) => void;
}

export function DesktopSourcePickerModal({isOpen, setOpen, setScreenShareSourceId}: DesktopSourcePickerProps) {
  const [sources, setSources] = useState<app.CaptureSource[]>([]);
  const [haveSourcesBeenFetched, setHaveSourcesBeenFetched] = useState(false);
  const [shouldDisplaySourceGrid, setShouldDisplaySourceGrid] = useState(false);

  useEffect(() => {
    async function fetchSources() {
      // Guard just in case
      const isDesktop = platform.todesktop.isDesktopApp();
      if (!isDesktop) {
        appLog('Non-desktop environment detected, use browser dialogue for source selection');
        setSources([]);
        setHaveSourcesBeenFetched(true);
        return;
      }

      // TODO: figure this out
      const status = await systemPreferences.getMediaAccessStatus('screen');

      if (status !== 'granted') {
        appLog('User has not granted screen share permission');
        return;
      }

      try {
        const sources = await app.getCaptureSources({
          types: ['window', 'screen'],
        });

        appLogO('Available capture sources', sources);

        setSources(sources);
        setHaveSourcesBeenFetched(true);
      } catch (e) {
        appLogO('error', e);
      }
    }

    if (!haveSourcesBeenFetched) {
      void fetchSources();
    }
  }, []);

  useEffect(() => {
    setShouldDisplaySourceGrid(haveSourcesBeenFetched && sources.length > 0);
  }, [sources, haveSourcesBeenFetched]);

  function handleSelectSource(source: app.CaptureSource) {
    appLog(`Setting source: ${source.id}`);
    setScreenShareSourceId(source.id);
  }

  return (
    <TransitionModal
      isOpen={isOpen}
      onClose={() => {
        setOpen(false);
      }}
      title="Select Source"
    >
      {shouldDisplaySourceGrid ? (
        <CaptureSourceGrid sources={sources} handleSelectSource={handleSelectSource} />
      ) : (
        'No sources detected. Please make sure you have allowed the app to record your screen.'
      )}
    </TransitionModal>
  );
}
